import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CircularProgress, IconButton, InputAdornment, TextField } from '@mui/material';
import {
  ErrorOutline,
  LockOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import Reveal from '../../atome/motion/reveal';
import { Eyebrow } from '../../atome/motion/motion.styled';
import { AuthServices } from '../../../api/auth';
import { useAuth } from '../../../context/authContext';
import { color } from '../../../theme/tokens';
import {
  BackLink,
  Card,
  Center,
  ErrorBanner,
  SignInWrapper,
  SubmitButton,
} from './signin.styled';

/** Same field treatment as the checkout form, so the two read as one product. */
const fieldSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    backgroundColor: 'rgba(255,255,255,0.7)',
    transition: 'box-shadow 240ms ease, background-color 240ms ease',
    '& fieldset': { borderColor: 'rgba(74, 112, 97, 0.2)' },
    '&:hover fieldset': { borderColor: color.forestSoft },
    '&.Mui-focused': { backgroundColor: '#fff', boxShadow: '0 8px 22px rgba(74,112,97,0.12)' },
    '&.Mui-focused fieldset': { borderColor: color.forest, borderWidth: '1px' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: color.forest },
};

const signInSchema = Yup.object().shape({
  email: Yup.string().email('That does not look like an email').required('Email is required'),
  password: Yup.string().required('Password is required'),
});

/**
 * Staff sign-in. Not linked from anywhere — you get here by typing /signin.
 * Only admins are let through; a client account is turned away rather than
 * signed in, since there is nothing behind this door for them.
 */
const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAdmin } = useAuth();
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  /** Where the guard bounced them from, so we can put them back. */
  const from = (location.state as { from?: string } | null)?.from || '/admin';

  // Already signed in as an admin — no reason to show the form again.
  if (isAdmin) return <Navigate to={from} replace />;

  const handleSubmit = async (values: { email: string; password: string }) => {
    setError('');
    try {
      const res = await AuthServices.handleLogin(values);

      if (res?.role !== 'admin') {
        setError('This account does not have admin access.');
        return;
      }

      // The API calls it `accessToken`; the auth context stores it as `token`.
      login({
        id: res.id,
        email: res.email,
        role: res.role,
        token: res.accessToken,
      });
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(typeof err === 'string' ? err : err?.message || 'Could not sign you in.');
    }
  };

  return (
    <SignInWrapper>
      <Center>
        <Reveal from="up">
          <Card>
            <Eyebrow>Klarity</Eyebrow>
            <h1>Sign in</h1>
            <p>This area is for the shop&rsquo;s administrators.</p>

            {error && (
              <ErrorBanner role="alert">
                <ErrorOutline fontSize="small" />
                {error}
              </ErrorBanner>
            )}

            <Formik
              initialValues={{ email: '', password: '' }}
              validationSchema={signInSchema}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                <Form noValidate>
                  <TextField
                    fullWidth
                    sx={fieldSx}
                    name="email"
                    type="email"
                    label="Email"
                    autoComplete="username"
                    autoFocus
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />

                  <TextField
                    fullWidth
                    sx={fieldSx}
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    autoComplete="current-password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              // Without this the field blurs on mousedown and Formik
                              // marks it touched, flashing "required" mid-typing.
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => setShowPassword((shown) => !shown)}
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                              edge="end"
                              size="small"
                              sx={{ color: color.inkFaint, '&:hover': { color: color.forest } }}
                            >
                              {showPassword ? (
                                <VisibilityOffOutlined fontSize="small" />
                              ) : (
                                <VisibilityOutlined fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <SubmitButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <CircularProgress size={18} sx={{ color: '#fff' }} />
                    ) : (
                      <>
                        <LockOutlined sx={{ fontSize: 16, mr: 0.8, verticalAlign: '-3px' }} />
                        Sign in
                      </>
                    )}
                  </SubmitButton>
                </Form>
              )}
            </Formik>

            <BackLink type="button" onClick={() => navigate('/')}>
              Back to the shop
            </BackLink>
          </Card>
        </Reveal>
      </Center>
    </SignInWrapper>
  );
};

export default SignIn;
